import { useState } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import { sha384 } from "crypto-hash";
import Countdown from "react-countdown";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useDynamicSecurityQuestionsOptions } from "hooks/useDynamicSecurityQuestionsOptions";

import { getUserProfile } from "service/userservice/getUserProfile";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";

import FormInputError from "components/General/FormInputError";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";
import SSNInput from "components/General/Form/SSNInput";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";

const ForgotUsername = () => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: false,
    defaultValues: {
      ssn: "",
      phoneNumber: "",
      socialSecurity_EIN: "",
    },
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [policyHolder, setPolicyHolder] = useState("individual");
  const onOptionChange = (e) => {
    setPolicyHolder(e.target.value);
    setInputType("policynumber");
  };
  const [inputType, setInputType] = useState("policynumber");
  const onInputTypeOptionChange = (e) => setInputType(e.target.value);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [name, setName] = useState("");
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [errorInfo, seterrorInfo] = useState("");
  const [modalState, setModalState] = useState(false);
  const [question1, setQuestion1] = useState(0);
  const [question2, setQuestion2] = useState(0);
  const [question3, setQuestion3] = useState(0);
  const [answer1, setAnswer1] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <Link
          to=""
          className="mb-4 mr-5"
          onClick={async () => {
            let passcode = randomNumberInRange(100000, 999999);
            setConfirmationPassCode(passcode.toString());
            setLoading(true);
            let securitycode = randomNumberInRange(100000, 999999);
            let sec =
              parseInt(securitycode.toString().substring(3, 4)) === 0
                ? 1
                : parseInt(securitycode.toString().substring(3, 4));
            let confirmationcode = passcode * sec;

            let contentDictionary = {};
            contentDictionary.confirmationcode = confirmationcode.toString();
            contentDictionary.username = name;
            contentDictionary.securitycode = securitycode.toString();

            await sendEmailNotification(userEmail, 2, contentDictionary).catch(
              (error) => {
                console.log(error);
              }
            );
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
          }}
        >
          Resend Code
        </Link>
      );
    } else {
      return (
        <p className="text-sm">
          Code expires in {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  async function onSubmit(data) {
    if (currentStep === 1) {
      setLoading(true);
      await getUserProfile(
        "",
        "customer",
        inputType === "policynumber" ? data.policyNumber : "",
        inputType === "policynumber"
          ? ""
          : policyHolder === "individual"
          ? data.ssn
          : data.socialSecurity_EIN,
        "",
        "",
        inputType === "policynumber"
          ? ""
          : policyHolder === "individual"
          ? data.firstname
          : data.businessName,
        inputType === "policynumber" ? "" : data.lastname
      )
        .then((userProfile) => {
          setLoading(false);
          if (userProfile === null) {
            seterrorInfo(t("ResetPassword-error-1"));
            setModalState(true);
          } else {
            setUserEmail(userProfile.emailId);
            setUserName(userProfile.userName);
            setName(userProfile.firstName);
            setQuestion1(userProfile.userSecurityAnswers[0].questionId);
            setQuestion2(userProfile.userSecurityAnswers[1].questionId);
            setQuestion3(userProfile.userSecurityAnswers[2].questionId);
            setAnswer1(userProfile.userSecurityAnswers[0].userAnswer);
            setAnswer2(userProfile.userSecurityAnswers[1].userAnswer);
            setAnswer3(userProfile.userSecurityAnswers[2].userAnswer);
            setCurrentStep(currentStep + 1);
          }
        })
        .catch((error) => {
          setLoading(false);
          if (error.status && error.status === 403) {
            seterrorInfo(error.title);
            setModalState(true);
          } else {
            seterrorInfo(t("ResetPassword-error-1"));
            setModalState(true);
            console.log(error);
          }
        });
    } else if (currentStep === 2) {
      let enteredanswer1 = (
        await sha384(
          data.securtityAnswerOne.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      let enteredanswer2 = (
        await sha384(
          data.securtityAnswerTwo.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      let enteredanswer3 = (
        await sha384(
          data.securtityAnswerThree.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      if (
        (answer1.toLowerCase() === enteredanswer1 &&
          answer2.toLowerCase() === enteredanswer2) ||
        (answer2.toLowerCase() === enteredanswer2 &&
          answer3.toLowerCase() === enteredanswer3) ||
        (answer1.toLowerCase() === enteredanswer1 &&
          answer3.toLowerCase() === enteredanswer3)
      ) {
        let passcode = randomNumberInRange(100000, 999999);
        setConfirmationPassCode(passcode.toString());
        setLoading(true);
        let securitycode = randomNumberInRange(100000, 999999);
        let sec =
          parseInt(securitycode.toString().substring(3, 4)) === 0
            ? 1
            : parseInt(securitycode.toString().substring(3, 4));
        let confirmationcode = passcode * sec;

        let contentDictionary = {};
        contentDictionary.confirmationcode = confirmationcode.toString();
        contentDictionary.username = name;
        contentDictionary.securitycode = securitycode.toString();

        await sendEmailNotification(userEmail, 2, contentDictionary).catch(
          (error) => {
            console.log(error);
          }
        );

        setLoading(false);
        setCurrentStep(currentStep + 1);
        setCurrentCounterTime(Date.now() + parseInt(config.OTP_EXPIRY_TIME));
      } else {
        seterrorInfo(t("ResetPassword-error-2"));
        setModalState(true);
      }
    } else if (currentStep === 3) {
      if (passcodeExpired) {
        seterrorInfo(t("ResetPassword-error-3"));
        setModalState(true);
      } else {
        if (
          data.resetCode === confirmationPassCode ||
          (config.ENABLE_DEFAULT_OTP === "true" &&
            data.resetCode === config.DEFAULT_OTP)
        ) {
          setCurrentStep(currentStep + 1);
        } else {
          seterrorInfo(t("ResetPassword-error-4"));
          setModalState(true);
        }
      }
    }
  }

  const uuid = uuidv4();
  const {
    setSecurityQuestionOneId,
    securityQuestionsOne,

    setSecurityQuestionTwoId,
    securityQuestionsTwo,

    setSecurityQuestionThreeId,
    securityQuestionsThree,
  } = useDynamicSecurityQuestionsOptions();

  useBreadcrumb([
    { name: "Home", url: "/" },
    { name: "Forgot username", url: "#" },
  ]);
  return (
    <>
      <EverlakeLoadingOverlay isLoading={loading}>
        {currentStep === 1 && (
          <>
            <div className="container ui-component-spacing">
              <div className="ui-card p-4 mx-auto lg:w-3/4 md:p-6">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  aria-label="forgot username form"
                >
                  <h2 className="font-body font-bold text-xl">
                    {t("ForgotUserName-title")}
                  </h2>

                  <fieldset>
                    <legend className="text-sm mb-2 font-light">
                      {t("ForgotUserName-formLegend")}
                    </legend>

                    <div className="inline-block mt-1 mr-6">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          type="radio"
                          name="policyOwnerType"
                          value="individual"
                          checked={policyHolder === "individual"}
                          onClick={onOptionChange}
                          id="ID_policyOwnerTypeIndividual"
                          {...register("policyOwnerType")}
                          className="mr-2"
                        />
                        {t("ForgotUserName-individualSelection")}
                      </label>
                    </div>
                    <div className="inline-block">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          type="radio"
                          name="policyOwnerType"
                          value="entity"
                          checked={policyHolder === "entity"}
                          onClick={onOptionChange}
                          id="ID_policyOwnerTypeEntity"
                          {...register("policyOwnerType")}
                          className="mr-2"
                        />
                        {t("ForgotUserName-entitySelection")}
                      </label>
                    </div>
                  </fieldset>

                  <hr />

                  {policyHolder === "individual" ? (
                    <fieldset>
                      <legend className="mb-4 font-semibold text-base">
                        {t("ForgotUserName-helpText-individual")}
                      </legend>

                      <div className="inline-block mt-1 mr-6 mb-8">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            value="policynumber"
                            checked={inputType === "policynumber"}
                            onClick={onInputTypeOptionChange}
                            id="ID_inputType_PN"
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-policyNumberSelection")}
                        </label>
                      </div>

                      <div className="inline-block">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            value="sinnumber"
                            checked={inputType === "sinnumber"}
                            onClick={onInputTypeOptionChange}
                            id="ID_inputType_SIN"
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-ssnSelection")}
                        </label>
                      </div>
                    </fieldset>
                  ) : (
                    <fieldset>
                      <legend className="mb-4 font-semibold text-base">
                        {t("ForgotUserName-helpText-entity")}
                      </legend>

                      <div className="inline-block mt-1 mr-6 mb-8">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            value="policynumber"
                            checked={inputType === "policynumber"}
                            onClick={onInputTypeOptionChange}
                            id="ID_inputType_PN"
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-policyNumberSelection")}
                        </label>
                      </div>

                      <div className="inline-block">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            value="einnumber"
                            checked={inputType === "einnumber"}
                            onClick={onInputTypeOptionChange}
                            id="ID_inputType_EIN"
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-ssnSelection-entity")}
                        </label>
                      </div>
                    </fieldset>
                  )}

                  {inputType === "policynumber" && (
                    <div>
                      <label
                        htmlFor={`${uuid}-ID_policyNumber`}
                        className="text-base font-semibold"
                      >
                        {t("ForgotUserName-formLabel-policyNumber")}
                      </label>
                      <p className="text-xs text-theme-text-tertiary mb-1">
                        {t("ForgotUserName-formLabel-policyNumber-helpText")}
                      </p>
                      <input
                        id={`${uuid}-ID_policyNumber`}
                        className="w-full"
                        type="text"
                        placeholder="#"
                        {...register("policyNumber", {
                          required: {
                            value: true,
                            message: "Please enter your contract number",
                          },
                        })}
                      />
                      {errors.policyNumber && (
                        <FormInputError
                          id={`${uuid}-policyNumber-error`}
                          errorMessage={errors.policyNumber.message}
                        />
                      )}
                    </div>
                  )}

                  {policyHolder === "individual" &&
                    inputType === "sinnumber" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        <div>
                          <div className="relative">
                            <label id={`${uuid}-ssn-label`}>
                              {t("ForgotUserName-formLabel-ssn")}
                            </label>

                            <SSNInput
                              name="ssn"
                              describedby={`${uuid}-ssn-label`}
                              register={register}
                              setValue={setValue}
                            />

                            {errors.ssn && (
                              <FormInputError
                                id={`${uuid}-ssn`}
                                errorMessage={errors.ssn.message}
                              />
                            )}
                          </div>
                          <div
                            id={`${uuid}-ssnnote`}
                            className="text-xxs text-theme-text-tertiary mt-1"
                          >
                            {t("ForgotUserName-formLabel-ssn-helpText")}
                          </div>{" "}
                        </div>
                        <div>
                          <label id={`${uuid}-firstname-label`}>
                            {t("ForgotUserName-formLabel-firstname")}
                          </label>
                          <input
                            id={`${uuid}-firstname`}
                            className="w-full"
                            type="text"
                            {...register("firstname", {
                              required: "Please enter your first name",
                              maxLength: {
                                value: 50,
                                message:
                                  "Please enter less than 50 characters for your firstname",
                              },
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message:
                                  "Please enter only [alphabets - '] characters",
                              },
                            })}
                            aria-describedby={`${uuid}-firstname-error`}
                            aria-labelledby={`${uuid}-firstname-label`}
                          />
                          {errors.firstname && (
                            <FormInputError
                              id={`${uuid}-firstname-error`}
                              errorMessage={errors.firstname.message}
                            />
                          )}
                        </div>

                        <div>
                          <label id={`${uuid}-lastname-label`}>
                            {t("ForgotUserName-formLabel-lastname")}
                          </label>
                          <input
                            id={`${uuid}-lastname`}
                            className="w-full"
                            type="text"
                            {...register("lastname", {
                              required: "please enter your last name",
                              maxLength: {
                                value: 50,
                                message:
                                  "Please enter less than 50 characters for your lastname",
                              },
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message:
                                  "Please enter only [alphabets - '] characters",
                              },
                            })}
                            aria-describedby={`${uuid}-lastname-error`}
                            aria-labelledby={`${uuid}-lastname-label`}
                          />
                          {errors.lastname && (
                            <FormInputError
                              id={`${uuid}-lastname-error`}
                              errorMessage={errors.lastname.message}
                            />
                          )}
                        </div>
                      </div>
                    )}

                  {policyHolder === "entity" && inputType === "einnumber" && (
                    <>
                      <div>
                        <div className="md:gap-6">
                          <label>
                            {t("ForgotUserName-formLabel-entityName")}
                          </label>
                          <input
                            id={`${uuid}-ID_businessName`}
                            className="w-full mb-4"
                            type="text"
                            placeholder="#"
                            {...register("businessName", {
                              required: {
                                value: true,
                                message: "please enter a business/trust name",
                              },
                            })}
                            aria-describedby={`${uuid}-businessName-error`}
                            aria-labelledby={`${uuid}-customer-businessName-id`}
                          />
                          {errors.businessName && (
                            <FormInputError
                              id={`${uuid}-businessName-error`}
                              errorMessage={errors.businessName.message}
                            />
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        <div>
                          <div className="relative">
                            <label id={`${uuid}-socialSecurity_EIN`}>
                              {t("ForgotUserName-formLabel-entitySsn")}
                            </label>

                            <SSNInput
                              name="socialSecurity_EIN"
                              describedby={`${uuid}-socialSecurity_EIN`}
                              register={register}
                              setValue={setValue}
                            />

                            {errors.socialSecurity_EIN && (
                              <FormInputError
                                id={`${uuid}-ID_ssn`}
                                errorMessage={errors.socialSecurity_EIN.message}
                              />
                            )}
                          </div>
                          <div
                            id={`${uuid}-ssnnote`}
                            className="text-xxs text-theme-text-tertiary mt-1"
                          >
                            {t("ForgotUserName-formLabel-ssn-helpText")}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="mt-4 text-right">
                    <button className="ui-btn-primary ui-btn-min-w mt-2">
                      {t("ForgotUserName-continueButton")}
                      <span className="sr-only"> onto sign up step 2</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

        {currentStep === 2 && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="container ui-component-spacing">
              <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
                <h2 className="font-body font-bold text-xl">
                  {t("ForgotUserName-step2-title")}
                </h2>
                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_question1Label`}>
                    {t("ResetPassword-label-2")}
                  </label>
                  <select
                    disabled
                    id="ID_question1"
                    className="w-full"
                    value={question1}
                    isOptionDisabled={true}
                    {...register("securityQuestionOne", {
                      onChange: (e) =>
                        setSecurityQuestionOneId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${uuid}-ID_question1Error`}
                    aria-labelledby={`${uuid}-ID_question1Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsOne.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  <div id="ID_question1Error">
                    {errors.securityQuestionOne && (
                      <div className="ui-input-error">
                        {errors.securityQuestionOne.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_answer1Label`}>
                    {t("ResetPassword-label-4")}
                  </label>
                  <input
                    id="ID_answer1"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerOne", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${uuid}-ID_answer1Error`}
                    aria-labelledby={`${uuid}-ID_answer1Label`}
                  />
                  <div id="ID_answer1Error">
                    {errors.securtityAnswerOne && (
                      <div className="ui-input-error">
                        {errors.securtityAnswerOne.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_question2Label`}>
                    {t("ResetPassword-label-5")}
                  </label>
                  <select
                    disabled
                    id="ID_question2"
                    className="w-full"
                    value={question2}
                    isOptionDisabled={true}
                    {...register("securityQuestionTwo", {
                      onChange: (e) =>
                        setSecurityQuestionTwoId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${uuid}-ID_question2Error`}
                    aria-labelledby={`${uuid}-ID_question2Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsTwo.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  <div id="ID_question2Error">
                    {errors.securityQuestionTwo && (
                      <div className="ui-input-error">
                        {errors.securityQuestionTwo.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_answer2Label`}>
                    {t("ResetPassword-label-13")}
                  </label>
                  <input
                    id="ID_answer2"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerTwo", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${uuid}ID_answer2Error`}
                    aria-labelledby={`${uuid}-ID_answer2Label`}
                  />
                  <div id="ID_answer2Error">
                    {errors.securtityAnswerTwo && (
                      <div className="ui-input-error">
                        {errors.securtityAnswerTwo.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_question3Label`}>
                    {t("ResetPassword-label-6")}
                  </label>
                  <select
                    disabled
                    id="ID_question3"
                    className="w-full"
                    value={question3}
                    isOptionDisabled={true}
                    {...register("securityQuestionThree", {
                      onChange: (e) =>
                        setSecurityQuestionThreeId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${uuid}-ID_question3Error`}
                    aria-labelledby={`${uuid}-ID_question3Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsThree.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  {errors.securityQuestionThree && (
                    <div className="ui-input-error">
                      {errors.securityQuestionThree.message}
                    </div>
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${uuid}-ID_answer3Label`}>
                    {t("ResetPassword-label-7")}
                  </label>
                  <input
                    id="ID_answer3"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerThree", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${uuid}-ID_answer3Error`}
                    aria-labelledby={`${uuid}-ID_answer3Label`}
                  />
                  <div id="ID_answer3Error">
                    {errors.securtityAnswerThree && (
                      <div className="ui-input-error">
                        {errors.securtityAnswerThree.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button className="ui-btn-primary ui-btn-min-w">
                    Continue
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {currentStep === 3 && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="container ui-component-spacing">
              <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
                <h2 className="font-body font-bold text-xl">
                  {t("ForgotUserName-step2-title")}
                </h2>
                <p className="text-sm">
                  <Trans
                    i18nKey="ForgotUserName-step3-helpText"
                    values={{
                      minute: parseInt(
                        parseInt(config.OTP_EXPIRY_TIME) / 60000
                      ),
                    }}
                  />
                </p>
                <div className="mt-4 md:mt-6">
                  <label
                    id="ID_passwordResetCodeLabel"
                    htmlFor="ID_passwordResetCode"
                  >
                    {t("ForgotUserName-username-reset-code")}
                  </label>
                  <input
                    id="ID_passwordResetCode"
                    aria-labelledby="ID_passwordResetCodeLabel"
                    aria-describedby="ID_passwordResetCodeError"
                    type="text"
                    autoComplete="off"
                    {...register("resetCode", {
                      required: {
                        value: true,
                        message: "please enter your code",
                      },
                      pattern: {
                        value: /^\d{6}$/,
                        message: "Please enter a valid code (6 digits)",
                      },
                    })}
                  />
                  {errors.resetCode && (
                    <FormInputError
                      id={`${uuid}-resetCode-error`}
                      errorMessage={errors.resetCode.message}
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <Countdown
                    date={currentCounterTime}
                    key={currentCounterIndex}
                    renderer={renderer}
                  />
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button className="ui-btn-primary ui-btn-min-w">
                    {t("ForgotUserName-continueButton")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {currentStep === 4 && (
          <>
            <div className="container ui-component-spacing">
              <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 xl:p-6">
                <h2 className="font-body font-bold text-xl">
                  {t("ForgotUserName-step4-title")}
                </h2>
                <hr />
                <p className="text-sm text-center">
                  {t("ForgotUserName-step4-subtitle")}
                </p>
                <div className="text-2xl text-center font-semibold my-2 xl:text-3xl">
                  {userName}
                </div>
                <div className="text-center mt-6 xl:mt-8">
                  <Link to="/">
                    <button className="ui-btn-primary ui-btn-min-w">
                      {t("ForgotUserName-toLogin")}
                    </button>
                  </Link>
                  <div className="mt-4">
                    <Link to="/forgot-password" className="text-xs">
                      {t("ForgotUserName-toForgotPassword")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <DomainErrorModal
          modalState={modalState}
          setModalState={setModalState}
          errorInfo={errorInfo}
        />
      </EverlakeLoadingOverlay>
    </>
  );
};

export default ForgotUsername;
